<template>
    <div class="settings-legal">
        <h2 class="settings-legal__title">{{ $t('settings.legal.title') }}</h2>
        <p class="settings-legal__desc">{{ $t('settings.legal.desc') }}</p>

        <div class="settings-legal__grid">
            <div class="settings-legal__field" :class="{ 'settings-legal__field--wide': field.wide }" v-for="field in fields" :key="field.key">
                <label class="settings-legal__label">{{ field.label }}</label>
                <input
                    class="settings-legal__input"
                    :class="{ 'settings-legal__input--invalid': fieldErrors[field.key] }"
                    :placeholder="field.placeholder"
                    :maxlength="field.maxlength"
                    v-model="form[field.key]"
                    :disabled="loading"
                />
                <span v-if="fieldErrors[field.key]" class="settings-legal__field-error">{{ fieldErrors[field.key] }}</span>
            </div>
        </div>

        <div class="settings-legal__footer">
            <span v-if="saved" class="settings-legal__saved">{{ $t('settings.legal.saved') }}</span>
            <span v-if="error" class="settings-legal__error">{{ error }}</span>
            <button
                class="settings-legal__btn"
                :disabled="loading || saving || hasErrors"
                @click="save"
            >
                {{ saving ? $t('settings.legal.saving') : $t('common.save') }}
            </button>
        </div>
    </div>
</template>

<script>
import Api from "@/api/api";

const api = new Api();

export default {
    name: "SettingsLegal",
    data() {
        return {
            loading: true,
            saving: false,
            saved: false,
            error: null,
            form: {
                legalName: "",
                inn: "",
                kpp: "",
                ogrn: "",
                legalAddress: "",
                phone: "",
                creditAddress: "",
                watcherLink: "",
                bankAccount: "",
                bankBik: "",
                bankName: "",
            },
        };
    },
    computed: {
        fields() {
            return [
                { key: "legalName",    label: this.$t("settings.legal.legalName"),    placeholder: this.$t("settings.legal.ph_legalName"),    maxlength: 256 },
                { key: "inn",          label: this.$t("settings.legal.inn"),           placeholder: this.$t("settings.legal.ph_inn"),           maxlength: 12 },
                { key: "kpp",          label: this.$t("settings.legal.kpp"),           placeholder: this.$t("settings.legal.ph_kpp"),           maxlength: 9 },
                { key: "ogrn",         label: this.$t("settings.legal.ogrn"),          placeholder: this.$t("settings.legal.ph_ogrn"),          maxlength: 15 },
                { key: "legalAddress", label: this.$t("settings.legal.legalAddress"),  placeholder: this.$t("settings.legal.ph_legalAddress"),  maxlength: 512, wide: true },
                { key: "phone",        label: this.$t("settings.legal.phone"),         placeholder: this.$t("settings.legal.ph_phone"),         maxlength: 32 },
                { key: "creditAddress", label: this.$t("settings.legal.creditAddress"), placeholder: this.$t("settings.legal.ph_creditAddress"), maxlength: 128, wide: true },
                { key: "watcherLink",  label: this.$t("settings.legal.watcher"),       placeholder: this.$t("settings.legal.ph_watcher"),       maxlength: 512, wide: true },
                { key: "bankAccount",  label: this.$t("settings.legal.bankAccount"),   placeholder: this.$t("settings.legal.ph_bankAccount"),   maxlength: 20 },
                { key: "bankBik",      label: this.$t("settings.legal.bankBik"),       placeholder: this.$t("settings.legal.ph_bankBik"),       maxlength: 9 },
                { key: "bankName",     label: this.$t("settings.legal.bankName"),      placeholder: this.$t("settings.legal.ph_bankName"),      maxlength: 256 },
            ];
        },

        fieldErrors() {
            const f = this.form;
            const errors = {};
            if (f.inn && !/^\d{10}$|^\d{12}$/.test(f.inn)) {
                errors.inn = this.$t("settings.legal.err_inn");
            }
            if (f.bankBik && !/^\d{9}$/.test(f.bankBik)) {
                errors.bankBik = this.$t("settings.legal.err_bik");
            }
            if (f.bankAccount && !/^\d{20}$/.test(f.bankAccount)) {
                errors.bankAccount = this.$t("settings.legal.err_account");
            }
            if (f.kpp && !/^\d{9}$/.test(f.kpp)) {
                errors.kpp = this.$t("settings.legal.err_kpp");
            }
            if (f.phone && !/^\+?[0-9][0-9\s\-()]{4,31}$/.test(f.phone.trim())) {
                errors.phone = this.$t("settings.legal.err_phone");
            }
            return errors;
        },

        hasErrors() {
            return Object.keys(this.fieldErrors).length > 0;
        },
    },
    async created() {
        try {
            const data = await api.getLegal();
            if (data) {
                Object.keys(this.form).forEach(k => {
                    if (data[k] != null) this.form[k] = data[k];
                });
            }
        } catch {
            // silently ignore — empty form on first load
        } finally {
            this.loading = false;
        }
    },
    methods: {
        async save() {
            this.saving = true;
            this.saved = false;
            this.error = null;
            try {
                await api.saveLegal({ ...this.form });
                this.saved = true;
                setTimeout(() => { this.saved = false; }, 3000);
            } catch {
                this.error = this.$t("settings.legal.error");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss">
.settings-legal {
    background: $white;
    border-radius: 16px;
    padding: 24px 40px 36px 40px;

    .settings-legal__title {
        font-size: 28px;
        line-height: 34px;
        color: $txt-main;
        margin-bottom: 8px;
    }

    .settings-legal__desc {
        @include golos-regular;
        font-size: 13px;
        color: $txt-light;
        line-height: 1.5;
        margin-bottom: 24px;
    }

    .settings-legal__grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px 40px;
        margin-bottom: 24px;
    }

    .settings-legal__field {
        display: flex;
        flex-direction: column;
        gap: 6px;

        &--wide {
            grid-column: 1 / -1;
        }
    }

    .settings-legal__label {
        @include golos-medium;
        font-size: 11px;
        color: $txt-light;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .settings-legal__input {
        @include golos-regular;
        font-size: 14px;
        color: $txt-main;
        border: 1px solid $gray;
        border-radius: 10px;
        padding: 10px 14px;
        outline: none;
        transition: border-color $transition;
        background: $bg-light;
        width: 100%;
        box-sizing: border-box;

        &:focus {
            border-color: $main;
        }

        &::placeholder {
            color: $txt-light;
            opacity: 0.7;
        }

        &:disabled {
            opacity: 0.5;
        }

        &.settings-legal__input--invalid {
            border-color: $status-red;
        }
    }

    .settings-legal__field-error {
        @include golos-regular;
        font-size: 11px;
        color: $status-red;
    }

    .settings-legal__footer {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .settings-legal__saved {
        @include golos-regular;
        font-size: 13px;
        color: $green;
    }

    .settings-legal__error {
        @include golos-regular;
        font-size: 13px;
        color: $status-red;
    }

    .settings-legal__btn {
        @include button-reset;
        @include golos-bold;
        font-size: 14px;
        color: $white;
        background: $main;
        border-radius: 12px;
        padding: 10px 28px;
        cursor: pointer;
        transition: opacity $transition;
        margin-left: auto;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &:hover:not(:disabled) {
            opacity: 0.85;
        }
    }
}

@media screen and (max-width: 1024px) {
    .settings-legal {
        .settings-legal__grid {
            grid-template-columns: 1fr;
        }
    }
}

@media screen and (max-width: 768px) {
    .settings-legal {
        padding: 21px 12px 28px 12px;

        .settings-legal__title {
            font-size: 24px;
            line-height: 29px;
            margin-bottom: 8px;
        }
    }
}
</style>
